import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ProfileCompletionDialog } from 'app/dialogs/profile-completion-dialog/profile-completion-dialog';
import { GET_USER_PROFILE } from 'environments/api.path';
import { firstValueFrom } from 'rxjs';
import { RequestService } from '@services/https/request.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';

@Injectable({ providedIn: 'root' })
export class AddPropertyAccessService {
  private readonly reminderCooldownMs = 24 * 60 * 60 * 1000;
  private readonly reminderStoragePrefix = 'add_property_profile_reminder_until_';

  constructor(
    private readonly request: RequestService,
    private readonly localStorage: LocalStorageService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly snackbar: SnackbarService,
  ) {}

  async openAddProperty(): Promise<void> {
    const storedUser = this.getStoredUser();

    if (!this.isLandlordOrOwner(storedUser)) {
      await this.router.navigate(['/user/add-property']);
      return;
    }

    try {
      const response: any = await firstValueFrom(
        this.request.GET(`${GET_USER_PROFILE}/${storedUser.userId}`, {}),
      );
      const latestUser = response?.data;

      if (!latestUser) {
        this.snackbar.notify('Unable to verify your profile. Please try again.', 2);
        return;
      }

      const profileForValidation = this.mergeProfileForValidation(latestUser, storedUser);
      this.saveLatestUser(storedUser, profileForValidation);

      if (this.isProfileComplete(profileForValidation) || this.isReminderDeferred(latestUser.userId || storedUser.userId)) {
        await this.router.navigate(['/user/add-property']);
        return;
      }

      const dialogRef = this.dialog.open(ProfileCompletionDialog, {
        disableClose: false,
      });
      const action = await firstValueFrom(dialogRef.afterClosed());

      if (action === 'complete-profile') {
        await this.router.navigate(['/user/profile'], { queryParams: { mode: 'edit' } });
      } else if (action === 'remind-later') {
        this.deferReminder(latestUser.userId || storedUser.userId);
        await this.router.navigate(['/user/add-property']);
      }
    } catch (error: any) {
      this.snackbar.notify(error?.message || 'Unable to verify your profile. Please try again.', 2);
    }
  }

  private getStoredUser(): any {
    try {
      return JSON.parse(this.localStorage.getUser() || '{}');
    } catch {
      return {};
    }
  }

  private isLandlordOrOwner(user: any): boolean {
    const role = String(user?.currentRole || user?.role || '').toLowerCase();
    return role === 'owner' || role === 'landlord';
  }

  private isProfileComplete(user: any): boolean {
    return [user?.firstName, user?.lastName, user?.profileImage, user?.phoneNumber]
      .every(value => this.hasValue(value));
  }

  private saveLatestUser(storedUser: any, latestUser: any): void {
    this.localStorage.setUser(JSON.stringify({ ...storedUser, ...latestUser }));
  }

  /**
   * The profile screen updates local storage immediately. Keep a just-saved
   * non-empty field if the user-details response is briefly behind that update.
   */
  private mergeProfileForValidation(latestUser: any, storedUser: any): any {
    const profile = { ...storedUser, ...latestUser };
    const requiredFields = ['firstName', 'lastName', 'profileImage', 'phoneNumber'];

    requiredFields.forEach(field => {
      if (!this.hasValue(latestUser?.[field]) && this.hasValue(storedUser?.[field])) {
        profile[field] = storedUser[field];
      }
    });

    return profile;
  }

  private hasValue(value: unknown): boolean {
    return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
  }

  private isReminderDeferred(userId: string | number | undefined): boolean {
    if (!userId) {
      return false;
    }

    const reminderUntil = Number(localStorage.getItem(`${this.reminderStoragePrefix}${userId}`));
    return reminderUntil > Date.now();
  }

  private deferReminder(userId: string | number | undefined): void {
    if (userId) {
      localStorage.setItem(
        `${this.reminderStoragePrefix}${userId}`,
        String(Date.now() + this.reminderCooldownMs),
      );
    }
  }
}
